/**
 * AttractScreen — passive marketing overlay shown while the tablet sits idle
 * in Kiosk Mode.
 *
 * Slide source (in priority order):
 *   1. Custom slides curated from the admin (`/api/kiosk/attract`):
 *      images, videos and product references.
 *   2. Fallback: products tagged `badge_premium` / `badge_bestseller`.
 *
 * Touch anywhere dismisses the overlay.
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { optimizedImageUrl } from '../lib/imageOptim';

const API = process.env.REACT_APP_BACKEND_URL || '';

const localizeName = (name, lang) => {
  if (!name) return '';
  if (typeof name === 'string') return name;
  return name[lang] || name.es || name.cat || Object.values(name)[0] || '';
};

const FALLBACK_TAGLINES = {
  es:  'Producte Local · Artesà · Amb Història',
  ca:  'Producte Local · Artesà · Amb Història',
  cat: 'Producte Local · Artesà · Amb Història',
  en:  'Local · Handcrafted · With History',
  fr:  'Local · Artisanal · Avec Histoire',
};

/**
 * Normalize a slide source into the unified shape used by the renderer.
 * `kind` is one of 'image' | 'video' | 'product'.
 */
const buildAutoSlides = (products, lang) => {
  const premium = (products || []).filter(p => p?.badge_premium && p?.image_url);
  const best    = (products || []).filter(p => p?.badge_bestseller && !p?.badge_premium && p?.image_url);
  return [...premium, ...best].slice(0, 8).map(p => ({
    id: `auto-${p.id}`,
    kind: 'product',
    duration_ms: 5000,
    productData: p,
    label: localizeName(p.name, lang),
    sublabel: p.badge_premium ? 'Selecció Premium' : 'Més sol·licitats',
    price: p.price,
    image_url: p.image_url,
  }));
};

const buildCustomSlides = (rawSlides, products, lang) => {
  return (rawSlides || []).map(s => {
    if (s.kind === 'image') {
      return {
        id: s.id, kind: 'image',
        duration_ms: s.duration_ms || 6000,
        image_url: `${API}/api/images/file/${s.image_id}`,
        label: s.label || '',
        sublabel: s.sublabel || '',
      };
    }
    if (s.kind === 'video') {
      return {
        id: s.id, kind: 'video',
        duration_ms: s.duration_ms || 8000,  // overridden by onEnded
        video_url: `${API}/api/kiosk/attract/video/${s.video_id}`,
        label: s.label || '',
        sublabel: s.sublabel || '',
      };
    }
    // product
    const prod = (products || []).find(p => p.id === s.product_id);
    if (!prod) return null;
    return {
      id: s.id, kind: 'product',
      duration_ms: s.duration_ms || 6000,
      productData: prod,
      label: s.label || localizeName(prod.name, lang),
      sublabel: s.sublabel || (prod.badge_premium ? 'Selecció Premium' : 'Més sol·licitats'),
      price: prod.price,
      image_url: prod.image_url,
    };
  }).filter(Boolean);
};


export const AttractScreen = ({ open, onDismiss }) => {
  const { language } = useLanguage();
  const [slides, setSlides] = useState([]);
  const [idx, setIdx] = useState(0);
  const advanceTimerRef = useRef(null);
  const videoRef = useRef(null);

  // Load custom slides + product catalogue together. Custom takes priority,
  // fallback to auto premium/bestseller if no custom slides exist.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          fetch(`${API}/api/kiosk/attract`),
          fetch(`${API}/api/products?limit=200`),
        ]);
        const customRaw = r1.ok ? await r1.json() : [];
        const products = r2.ok ? await r2.json() : [];
        if (cancelled) return;
        const custom = buildCustomSlides(customRaw, products, language);
        const final = custom.length > 0
          ? custom
          : buildAutoSlides(products, language);
        setSlides(final);
        setIdx(0);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [open, language]);

  const slide = useMemo(() => slides[idx] || null, [slides, idx]);

  // Schedule auto-advance based on the slide's duration. For videos we wait
  // for the `ended` event instead, so this timer is a hard upper bound only.
  const armAdvance = useCallback((ms) => {
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    if (!slides.length) return;
    advanceTimerRef.current = setTimeout(() => {
      setIdx(i => (i + 1) % slides.length);
    }, Math.max(2000, ms));
  }, [slides.length]);

  useEffect(() => {
    if (!open || !slide) return;
    // Videos: cap at min(duration_ms || 30s, 30s) — `ended` fires earlier
    // if the video is shorter.
    const ms = slide.kind === 'video' ? 30000 : (slide.duration_ms || 6000);
    armAdvance(ms);
    return () => {
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    };
  }, [open, slide, armAdvance]);

  if (!open) return null;
  if (!slide) {
    // Slides not loaded yet (or none and no fallback). Render a neutral logo
    // screen rather than nothing so the user sees the kiosk is "alive".
    return (
      <div
        data-testid="kiosk-attract-screen"
        onPointerDown={onDismiss}
        style={{
          position: 'fixed', inset: 0, zIndex: 99990,
          background: '#0E0B07', color: '#F9F6F0',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: "'Playfair Display', serif",
          fontSize: 'clamp(60px, 10vw, 120px)',
          letterSpacing: 4, cursor: 'pointer',
        }}
      >
        PONT<span style={{ color: '#C89B3C' }}>10</span>
      </div>
    );
  }

  const tagline = FALLBACK_TAGLINES[language] || FALLBACK_TAGLINES.es;
  const isImage = slide.kind === 'image';
  const isVideo = slide.kind === 'video';
  const bg = !isVideo && slide.image_url
    ? (isImage ? optimizedImageUrl(slide.image_url, 1600) : optimizedImageUrl(slide.image_url, 1600))
    : '';

  return (
    <div
      data-testid="kiosk-attract-screen"
      onPointerDown={onDismiss}
      style={{
        position: 'fixed', inset: 0, zIndex: 99990,
        background: '#0E0B07', overflow: 'hidden', cursor: 'pointer',
        animation: 'attractFadeIn 0.6s ease both',
      }}
    >
      {/* Background — image (Ken Burns) or autoplay muted video */}
      {isVideo ? (
        <video
          key={slide.id}
          ref={videoRef}
          src={slide.video_url}
          autoPlay
          muted
          playsInline
          onEnded={() => setIdx(i => (i + 1) % slides.length)}
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
            filter: 'brightness(0.7)',
          }}
        />
      ) : bg ? (
        <div
          key={slide.id}
          aria-hidden="true"
          style={{
            position: 'absolute', inset: 0,
            backgroundImage: `url(${bg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.6)',
            animation: `kenBurns ${(slide.duration_ms || 6000) + 500}ms ease-in-out both`,
          }}
        />
      ) : null}

      {/* Gradient veil */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, rgba(14,11,7,0.2) 0%, rgba(14,11,7,0.85) 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* Foreground */}
      <div
        style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
          padding: 'clamp(32px, 5vw, 64px)',
          color: '#F9F6F0', fontFamily: "'DM Sans', sans-serif",
          pointerEvents: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <p style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(40px, 6vw, 84px)',
            letterSpacing: 2, margin: 0, lineHeight: 1,
          }}>
            PONT<span style={{ color: '#C89B3C', textShadow: '0 0 24px rgba(200,155,60,0.6)' }}>10</span>
          </p>
          <p style={{
            fontSize: 'clamp(13px, 1.4vw, 18px)',
            letterSpacing: '0.3em', color: 'rgba(249,246,240,0.7)',
            margin: 0, textTransform: 'uppercase',
          }}>
            Besal&uacute;
          </p>
        </div>

        <div key={`cap-${slide.id}`} style={{ animation: 'captionUp 0.7s ease both' }}>
          {slide.sublabel && (
            <p style={{
              fontSize: 'clamp(12px, 1.4vw, 18px)',
              letterSpacing: '0.32em',
              color: '#C89B3C', textTransform: 'uppercase',
              margin: '0 0 16px',
            }}>
              {slide.sublabel}
            </p>
          )}
          {slide.label && (
            <p style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 'clamp(34px, 5.2vw, 76px)',
              fontWeight: 600, lineHeight: 1.05,
              margin: 0, maxWidth: '80vw',
              textShadow: '0 4px 32px rgba(0,0,0,0.6)',
            }}>
              {slide.label}
            </p>
          )}
          {slide.price ? (
            <p style={{
              fontSize: 'clamp(20px, 2.6vw, 36px)',
              color: '#F9F6F0', marginTop: 16, fontWeight: 500,
            }}>
              {Number(slide.price).toFixed(2)}€
            </p>
          ) : null}
          <p style={{
            marginTop: 36,
            fontSize: 'clamp(13px, 1.3vw, 18px)',
            letterSpacing: '0.18em',
            color: 'rgba(249,246,240,0.65)',
          }}>
            {tagline}
          </p>
          <p
            data-testid="kiosk-attract-tap-hint"
            style={{
              marginTop: 12,
              fontSize: 'clamp(11px, 1.05vw, 14px)',
              letterSpacing: '0.4em',
              color: 'rgba(249,246,240,0.45)',
              textTransform: 'uppercase',
            }}
          >
            · Toca per comen&ccedil;ar ·
          </p>
        </div>
      </div>

      <style>{`
        @keyframes attractFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes kenBurns {
          0%   { transform: scale(1.04) translate(0, 0); }
          100% { transform: scale(1.14) translate(-1.5%, -1.5%); }
        }
        @keyframes captionUp {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default AttractScreen;
